import { useState, useEffect } from "react";
import { toast } from "react-toastify";

const DEFAULT_THEME = {
  "--accent":   "#984A1F",
  "--main-bg":  "#F7F7FA",
  "--card-bg":  "#FFFFFF",
  "--nav-text": "#2D3142",
  "--subtitle": "#6B7280",
  "--price":    "#222222",
  "--border":   "#E5E7EB",
};

const LABELS = {
  "--accent":   "Accent / Brand Color",
  "--main-bg":  "Page Background",
  "--card-bg":  "Card Background",
  "--nav-text": "Text / Navbar Color",
  "--subtitle": "Subtitle / Muted Text",
  "--price":    "Price Text Color",
  "--border":   "Border Color",
};

const STORAGE_KEY = "site_theme";

const applyTheme = (theme) => {
  Object.entries(theme).forEach(([key, val]) => {
    document.documentElement.style.setProperty(key, val);
  });
};

const ThemeManager = () => {
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...DEFAULT_THEME, ...JSON.parse(saved) } : { ...DEFAULT_THEME };
    } catch { return { ...DEFAULT_THEME }; }
  });

  const [saved, setSaved] = useState(false);

  useEffect(() => { applyTheme(theme); }, [theme]);

  const handleChange = (key, val) => {
    setTheme(prev => ({ ...prev, [key]: val }));
    setSaved(false);
  };

  const handleSave = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(theme));
    applyTheme(theme);
    setSaved(true);
    toast.success("Theme saved! Colors applied to website.");
  };

  const handleReset = () => {
    setTheme({ ...DEFAULT_THEME });
    localStorage.removeItem(STORAGE_KEY);
    applyTheme(DEFAULT_THEME);
    setSaved(false);
    toast.info("Theme reset to defaults.");
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h5 className="fw-bold mb-1">Website Theme</h5>
          <p className="text-muted small mb-0">Changes apply instantly to the frontend</p>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary rounded-pill px-4" onClick={handleReset}>
            <i className="bi bi-arrow-counterclockwise me-2"></i>Reset
          </button>
          <button className="btn btn-dark rounded-pill px-4" style={{ background: "#2f0f0c" }} onClick={handleSave}>
            <i className={`bi bi-${saved ? "check-circle" : "save"} me-2`}></i>
            {saved ? "Saved!" : "Save Theme"}
          </button>
        </div>
      </div>

      <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
        <table className="table table-hover mb-0">
          <thead className="table-light">
            <tr>
              <th className="border-0 fw-semibold ps-4">Color Name</th>
              <th className="border-0 fw-semibold">Variable</th>
              <th className="border-0 fw-semibold">Preview</th>
              <th className="border-0 fw-semibold">Hex Code</th>
              <th className="border-0 fw-semibold">Pick</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(LABELS).map(([key, label]) => (
              <tr key={key}>
                <td className="ps-4 fw-semibold align-middle">{label}</td>
                <td className="align-middle"><code className="small text-muted">{key}</code></td>
                <td className="align-middle">
                  <div className="rounded-2 border" style={{ width: 36, height: 36, background: theme[key] }} />
                </td>
                <td className="align-middle">
                  <input
                    type="text"
                    className="form-control form-control-sm rounded-3 font-monospace"
                    style={{ width: 110 }}
                    value={theme[key]}
                    maxLength={7}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) handleChange(key, val);
                    }}
                    placeholder="#000000"
                  />
                </td>
                <td className="align-middle">
                  <input
                    type="color"
                    className="form-control form-control-color rounded-2"
                    style={{ width: 44, height: 34, padding: 2, cursor: "pointer" }}
                    value={theme[key]}
                    onChange={(e) => handleChange(key, e.target.value)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Live Preview */}
      <div className="card border-0 shadow-sm rounded-4 p-4 mt-4">
        <h6 className="fw-bold mb-3">Live Preview</h6>
        <div className="d-flex flex-wrap gap-3 align-items-center p-4 rounded-3" style={{ background: theme["--main-bg"] }}>
          <div className="p-3 rounded-3 shadow-sm" style={{ background: theme["--card-bg"], border: `1px solid ${theme["--border"]}` }}>
            <p className="mb-1 fw-bold" style={{ color: theme["--nav-text"] }}>Product Name</p>
            <p className="mb-2 small" style={{ color: theme["--subtitle"] }}>Premium fragrance</p>
            <p className="mb-2 fw-bold" style={{ color: theme["--price"] }}>₹1,299</p>
            <button className="btn btn-sm rounded-pill px-3" style={{ background: theme["--accent"], color: "#fff", border: "none" }}>
              Add to Cart
            </button>
          </div>
          <div className="p-3 rounded-3" style={{ background: theme["--accent"] }}>
            <p className="mb-0 text-white fw-bold">Accent Button</p>
          </div>
          <div className="p-3 rounded-3" style={{ background: theme["--card-bg"], border: `2px solid ${theme["--border"]}` }}>
            <p className="mb-0" style={{ color: theme["--subtitle"] }}>Muted text sample</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemeManager;
